import { BUTTON_VARIANTS, Checkbox, Loader } from 'cdk-radial';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  FileType,
  Z_INDEX_FOR_DIALOG,
  Z_INDEX_FOR_DROPDOWN
} from 'common/constants';
import { ComposedTableDataType } from 'components/ComposedTable/types';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { labels, messages } from 'components/ExportOrArchiveDialog/messages';
import printGridData from 'components/PrintViewer/PdfExport/printGridData';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { Toast } from 'components/Toast';
import { useFlagValue } from 'hooks';
import {
  useFetchPrinterList,
  useSubmitPrintData
} from 'hooks/useClientDataServices';
import { useValidation } from 'hooks/useValidation';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/selectors';
import { getSelectedVendorInvoicePairs } from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { labelMessages } from './messages';
import {
  DialogContent,
  StyledContainer,
  StyledMyPortalCopiesMenu,
  StyledPrintDialog,
  StyledMyPortalPrinter,
  StyledMyPortalTrayMenu,
  Container,
  StyledToast
} from './styled';
import {
  ButtonVariants,
  DropdownType,
  PrinterType,
  PrintViewerProps,
  TrayPaperSizeType
} from './types';
import { defaultPrinter, getPaperSizeOptions, getTrayOptions } from './utils';

export const PrintViewer = <
  TData extends ComposedTableDataType,
  TExportData extends ComposedTableDataType = Record<string, never>
>({
  exportData,
  isExportDataFetching,
  getPDFDocument,
  rowModelType,
  gridRef,
  open,
  onClose,
  setIsExportDataQueryEnabled,
  totalsInFooter,
  tableHeaderFilters
}: PrintViewerProps<TData, TExportData>): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const [isFileGenerationError, setIsFileGenerationError] =
    useState<boolean>(false);
  const [printer, setPrinter] = useState<PrinterType>(defaultPrinter);
  const [selectedPrinter, setSelectedPrinter] = useState<DropdownType>();
  const [copies, setCopies] = useState<DropdownType>();
  const [paperSizeList, setPaperSizeList] = useState<DropdownType[]>([]);
  const [paperSize, setPaperSize] = useState<DropdownType>();
  const [trayOptions, setTrayOptions] = useState<TrayPaperSizeType[]>([]);
  const [selectedTray, setSelectedTray] = useState<DropdownType>();
  const [isTwoSidedPrint, setIsTwoSidedPrint] = useState<boolean>(false);
  const [isExportAsPDFLoading, setIsExportAsPDFLoading] =
    useState<boolean>(false);
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);
  const copiesOption = printer?.printerAlias ? [1, 2, 3, 4, 5] : [];
  const selectedTab = useAppSelector(getSelectedTab);
  const activeAuthorizePaymentPage = useAppSelector(
    getActiveAuthorizePaymentPage
  );

  const selectedVendorInvoicePairs = useAppSelector(
    getSelectedVendorInvoicePairs
  );
  const interCompanyFlag = useFlagValue('dmsAccountingIntercompanyPayment');

  const { data, isLoading } = useFetchPrinterList(open);

  const { hasError: hasPrinterError, validate: validatePrinter } =
    useValidation();

  const { hasError: hasPaperSizeError, validate: validatePaperSize } =
    useValidation();

  const { hasError: hasTrayError, validate: validateTray } = useValidation();

  const { hasError: hasCopiesError, validate: validateCopies } =
    useValidation();

  const { mutate: mutatePostPrintData, isLoading: isLoadingPrint } =
    useSubmitPrintData();
  const intl = useIntl();

  const postPrintData = useCallback(
    (documentBase64encoded: string) => {
      mutatePostPrintData(
        {
          copies: copies?.value,
          documentBase64encoded,
          duplex: isTwoSidedPrint,
          printerId: printer?.printerId,
          trayId: selectedTray?.value
        },
        {
          onSuccess: () => {
            onClose();
            dispatch(
              setSuccessMessage(
                intl.formatMessage(labelMessages.printSuccessMessage)
              )
            );
          },
          onError: () => {
            onClose();
            dispatch(
              setErrorMessages([
                intl.formatMessage(labelMessages.printErrorMessage)
              ])
            );
          },
          onSettled: () => {
            setIsPrintLoading(false);
          }
        }
      );
    },
    [
      copies?.value,
      dispatch,
      intl,
      isTwoSidedPrint,
      mutatePostPrintData,
      onClose,
      printer?.printerId,
      selectedTray?.value
    ]
  );

  const handlePrint = useCallback(async () => {
    const isValidPrinter = validatePrinter(selectedPrinter?.value);
    const isValidPaperSize = validatePaperSize(paperSize?.value);
    const isValidTray = validateTray(selectedTray?.value);
    const isValidCopies = validateCopies(copies?.value);

    if (isValidPrinter && isValidPaperSize && isValidTray && isValidCopies) {
      if (rowModelType !== 'serverSide') {
        if (gridRef) {
          const printBase64 = await printGridData(
            gridRef,
            {
              selectedTab,
              intl,
              totalsInFooter,
              tableHeaderFilters,
              activeAuthorizePaymentPage
            },
            selectedVendorInvoicePairs,
            false,
            interCompanyFlag
          );
          if (printBase64) {
            postPrintData(printBase64);
          }
        }
      } else {
        setIsPrintLoading(true);
        setIsExportDataQueryEnabled?.(true);
      }
    }
  }, [
    activeAuthorizePaymentPage,
    copies?.value,
    gridRef,
    interCompanyFlag,
    intl,
    paperSize?.value,
    postPrintData,
    rowModelType,
    selectedPrinter?.value,
    selectedTab,
    selectedTray?.value,
    selectedVendorInvoicePairs,
    setIsExportDataQueryEnabled,
    tableHeaderFilters,
    totalsInFooter,
    validateCopies,
    validatePaperSize,
    validatePrinter,
    validateTray
  ]);

  const handleExportAsPDF = useCallback(async () => {
    setIsExportAsPDFLoading(true);
    if (rowModelType !== 'serverSide') {
      if (gridRef) {
        await printGridData(
          gridRef,
          {
            selectedTab,
            intl,
            totalsInFooter,
            tableHeaderFilters,
            activeAuthorizePaymentPage
          },
          selectedVendorInvoicePairs,
          true,
          interCompanyFlag
        );
        setIsExportAsPDFLoading(false);
      }
    } else {
      setIsExportDataQueryEnabled?.(true);
    }
  }, [
    activeAuthorizePaymentPage,
    gridRef,
    interCompanyFlag,
    intl,
    rowModelType,
    selectedTab,
    selectedVendorInvoicePairs,
    setIsExportDataQueryEnabled,
    tableHeaderFilters,
    totalsInFooter
  ]);

  const handleExportDataResponse = useCallback(async () => {
    async function handlePDFExport(): Promise<Blob | undefined> {
      let pdfBlob: Blob | undefined;
      if (!exportData || !getPDFDocument) return pdfBlob;

      try {
        pdfBlob = await getPDFDocument(exportData, true);
      } catch (err) {
        console.error(`Error generating or downloading pdf:`, err);
        setIsFileGenerationError(true);
      }
      return pdfBlob;
    }

    if (!exportData || isExportDataFetching || !open || !getPDFDocument) return;

    if (isExportAsPDFLoading) {
      const pdfBlob = await handlePDFExport();
      setIsExportAsPDFLoading(false);
      if (pdfBlob === undefined) {
        return;
      }
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfBlob);
      link.download = 'export.pdf';
      link.click();
    } else if (isPrintLoading) {
      let pdfData: string | undefined;
      try {
        pdfData = await getPDFDocument(exportData, false);
      } catch (err) {
        console.error(`Error generating or downloading pdf:`, err);
        setIsFileGenerationError(true);
      }
      if (pdfData === undefined) {
        return;
      }
      postPrintData(pdfData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData, isExportDataFetching]);

  const buttonsForDialog = [
    {
      id: 'export-as-pdf',
      onClick: handleExportAsPDF,
      isDisabled: isExportAsPDFLoading,
      isLoading: isExportAsPDFLoading,
      text: intl.formatMessage(labelMessages.exportAsPDF),
      variant: (BUTTON_VARIANTS as ButtonVariants).TEXT
    },
    {
      id: 'print',
      onClick: handlePrint,
      isDisabled: isPrintLoading,
      isLoading: isPrintLoading,
      text: intl.formatMessage(labelMessages.printPdf),
      variant: (BUTTON_VARIANTS as ButtonVariants).PRIMARY
    }
  ];

  useEffect(() => {
    if (printer) {
      const paperSizeOptions = getPaperSizeOptions(printer);
      setPaperSizeList(paperSizeOptions);
    }
  }, [printer]);

  useEffect(() => {
    if (printer) {
      const trays = getTrayOptions(printer, paperSize);
      setTrayOptions(trays);
    }
  }, [printer, paperSize]);

  useEffect(() => {
    handleExportDataResponse();
  }, [handleExportDataResponse]);

  const handleCopiesChange = (selectedValue: DropdownType): void => {
    setCopies(selectedValue);
    validateCopies(selectedValue?.value);
  };

  const handlePrinterChange = (selectedValue: DropdownType): void => {
    if (
      data?.printers?.filter(item => item.printerId === selectedValue.value)[0]
    ) {
      setPrinter(
        data?.printers?.filter(
          item => item.printerId === selectedValue.value
        )[0]
      );
    }
    setSelectedPrinter(selectedValue);
    validatePrinter(selectedValue.value);
  };

  const handlePaperSizeChange = (selectedValue: DropdownType): void => {
    setPaperSize(selectedValue);
    validatePaperSize(selectedValue.value);
  };

  const handleTrayChange = (selectedValue: DropdownType): void => {
    setSelectedTray(selectedValue);
    validateTray(selectedValue.value);
  };

  return (
    <>
      <StyledPrintDialog
        buttonsProps={
          !(isLoading || isLoadingPrint) ? buttonsForDialog : undefined
        }
        handleClose={() => {
          if (!isPrintLoading && !isExportAsPDFLoading) {
            onClose();
          }
        }}
        title={intl.formatMessage(labelMessages.print)}
        open={open}
        dataTestId="print-checks-dialog"
        id="print-checks-dialog"
      >
        {isFileGenerationError && (
          <StyledToast>
            <Toast
              buttonProps={[
                {
                  text: intl.formatMessage(labels.dismiss),
                  onClick: () => {
                    setIsFileGenerationError(false);
                  }
                }
              ]}
              content={intl.formatMessage(
                messages.errorMessageForFileGeneration,
                { fileType: FileType.PDF }
              )}
            />
          </StyledToast>
        )}
        {isLoading || isLoadingPrint ? (
          <StyledContainer>
            <Loader
              data-testid="loader"
              variant="inline"
              label={intl.formatMessage(labels.loadingLabel)}
              style={{
                height: '23vh'
              }}
            />
          </StyledContainer>
        ) : (
          <>
            <Container id="containerID" />
            <DialogContent>
              <StyledMyPortalPrinter
                isOpen={open}
                isRequired
                inputId="printer-select-dropdown"
                dataTestId="printer-select-dropdown"
                label={intl.formatMessage(labelMessages.printer)}
                placeHolder={intl.formatMessage(
                  labelMessages.placeHolderSelect
                )}
                onChange={handlePrinterChange}
                options={
                  data?.printers.map(item => ({
                    label: item.printerAlias,
                    value: item.printerId
                  })) || []
                }
                styles={{
                  zIndex: Z_INDEX_FOR_DIALOG
                }}
                value={selectedPrinter}
                enableCustomValidation
                hasError={hasPrinterError}
                errorMessage={intl.formatMessage(
                  labelMessages.printerErrorText
                )}
                zIndexPrinter={Z_INDEX_FOR_DROPDOWN}
              />

              <StyledMyPortalPrinter
                isOpen={open}
                isRequired
                inputId="paper-select-dropdown"
                dataTestId="paper-select-dropdown"
                label={intl.formatMessage(labelMessages.paperSize)}
                placeHolder={intl.formatMessage(
                  labelMessages.placeHolderSelect
                )}
                onChange={handlePaperSizeChange}
                options={paperSizeList}
                styles={{
                  zIndex: Z_INDEX_FOR_DIALOG
                }}
                value={paperSize}
                enableCustomValidation
                hasError={hasPaperSizeError}
                errorMessage={intl.formatMessage(
                  labelMessages.paperSizeErrorText
                )}
                zIndexPrinter={Z_INDEX_FOR_DROPDOWN}
              />

              <StyledMyPortalTrayMenu
                isOpen={open}
                isRequired
                inputId="tray-select-dropdown"
                dataTestId="tray-select-dropdown"
                label={intl.formatMessage(labelMessages.tray)}
                placeHolder={intl.formatMessage(
                  labelMessages.placeHolderSelect
                )}
                onChange={handleTrayChange}
                options={trayOptions.map(item => ({
                  label: item.trayId,
                  value: item.trayName
                }))}
                styles={{
                  zIndex: Z_INDEX_FOR_DIALOG
                }}
                value={selectedTray}
                enableCustomValidation
                hasError={hasTrayError}
                errorMessage={intl.formatMessage(labelMessages.trayErrorText)}
                zIndexPrinter={Z_INDEX_FOR_DROPDOWN}
              />
              <StyledMyPortalCopiesMenu
                isOpen={open}
                isRequired
                inputId="copy-input"
                dataTestId="copy-input"
                label={intl.formatMessage(labelMessages.copies)}
                placeHolder={intl.formatMessage(
                  labelMessages.placeHolderSelect
                )}
                onChange={handleCopiesChange}
                options={copiesOption.map(item => ({
                  label: item.toString(),
                  value: item.toString()
                }))}
                styles={{
                  zIndex: Z_INDEX_FOR_DIALOG
                }}
                value={copies}
                enableCustomValidation
                hasError={hasCopiesError}
                errorMessage={intl.formatMessage(labelMessages.copiesErrorText)}
                zIndexPrinter={Z_INDEX_FOR_DROPDOWN}
              />
            </DialogContent>
            <Checkbox
              label={intl.formatMessage(labelMessages.twoSidedPrinting)}
              onClick={(eve: ChangeEvent<HTMLInputElement>) =>
                setIsTwoSidedPrint(eve.target.checked)
              }
              checked={isTwoSidedPrint}
            />
          </>
        )}
      </StyledPrintDialog>
    </>
  );
};
